<template>
  <div
    ref="slide"
    :class="{
      'is-active': isActiveOrEditMode,
      'is-editmode': isEditMode,
    }"
    class="carousel-slide"
    @transitionend="onSlideTransitionEnd"
  >
    <div class="relative overflow-hidden">
      <div class="img-container relative aspect-[6/7] max-h-screen w-full md:aspect-video">
        <PictureElement
          :breakpoints="BREAKPOINTS"
          :class="{
            hidden: containsImage(imageMobile),
            'md:inline': containsImage(imageMobile),
          }"
          :img="image"
          :loading="isActive ? 'eager' : 'lazy'"
          img-class=" absolute inset-0  block object-cover w-full h-full"
        />
        <PictureElement
          v-if="containsImage(imageMobile)"
          :breakpoints="BREAKPOINTS"
          :img="imageMobile"
          :loading="isActive ? 'eager' : 'lazy'"
          class="md:hidden"
          img-class=" absolute inset-0 block object-cover w-full h-full"
        />
        <div
          :class="contentToTailwindClasses({ opacity: opacity })"
          class="invisible absolute inset-x-0 left-0 top-0 h-full w-8/12 bg-gradient-to-r from-black via-black to-transparent md:visible md:w-2/3"
        />
        <DotPattern
          v-if="dotsVisible || isEditMode"
          ref="dots"
          :variant="dotPattern"
          class="absolute inset-0 block h-full w-full"
        />
      </div>
    </div>
    <div class="bg-light-beige md:absolute md:inset-0 md:bg-transparent">
      <div
        class="text-container container flex flex-col items-center justify-center py-8 text-center md:h-full md:items-start md:pb-0 md:text-left"
      >
        <div class="mb-6">
          <Heading
            v-if="headline.text"
            color="blue"
            class="md:text-white"
            :size="headingSizeMapping[headline?.tag ?? 'h2']"
            :tag="headline?.tag ?? 'h2'"
          >
            {{ headline.text }}
          </Heading>
          <Heading
            v-if="text"
            class="md:text-white"
            :size="headingSizeMapping[text?.tag ?? 'h3']"
            :tag="text?.tag ?? 'h3'"
            weight="normal"
          >
            {{ text.text }}
          </Heading>
        </div>
        <CallToActionComponent
          v-if="link?.label ?? false"
          :link="link"
          :stretch-link="stretchLink"
          class="text-sm lg:py-6 lg:text-base"
          @click="trackPromoClick"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Heading } from "~/components/clarity-ui/Heading";
import usePromoTracking from "~/composables/tracking/usePromoTracking";
import CallToActionComponent from "~/templates/components/cms/CallToActionComponent.vue";
import DotPattern from "~/templates/elements/DotPattern.vue";
import PictureElement from "~/templates/elements/PictureElement.vue";
import { isComponentPreview, isEditMode as evaluateEditMode } from "~/utils/content/magnolia";
import { DIMENSIONS } from "~/utils/helper/imaging";
import { containsImage } from "~/utils/helper/magnolia";
import { contentToTailwindClasses } from "~/utils/helper/tailwind";

const props = defineProps([
  "text",
  "headline",
  "image",
  "imageTablet",
  "imageMobile",
  "link",
  "isActive",
  "dotPattern",
  "opacity",
  "navText",
  "metadata",
  "stretchLink",
]);

const isEditMode = evaluateEditMode();
const dotsVisible = ref(false);
const slide = ref(null);
const isActive = toRef(() => props.isActive);

const { trackViewPromotionIfVisibleAndActive, trackPromoClick } = usePromoTracking({
  metadata: props.metadata,
  promoIndex: props.index,
  creativeName: props.navText,
});
const onSlideTransitionEnd = () => {
  dotsVisible.value = props.isActive;
};
const isActiveOrEditMode = computed(() => {
  return props.isActive || isEditMode || isComponentPreview();
});

const BREAKPOINTS = {
  start: DIMENSIONS["start"].ratio["6/7"],
  sm: DIMENSIONS["sm"].ratio["6/7"],
  md: DIMENSIONS["md"].ratio.video,
  xl: DIMENSIONS["xl"].ratio.video,
  container: DIMENSIONS["container"].ratio.video,
  page: DIMENSIONS["page"].ratio["video"],
};

onMounted(() => {
  trackViewPromotionIfVisibleAndActive(slide, isActive);
});

// todo: these mappings are necessary to map the Magnolia datafeed to component's interface
const headingSizeMapping = { h1: "xxxlarge", h2: "xxlarge", h3: "xlarge", h4: "large" };
</script>

<style scoped>
.carousel-slide {
  @apply pointer-events-none opacity-0 motion-safe:transition-opacity motion-safe:duration-2000;
  @apply absolute inset-0;
}

.img-container {
  @apply scale-105 motion-safe:transition-all motion-safe:duration-2000;
}

.text-container {
  @apply opacity-0 motion-safe:transition-all motion-safe:duration-300 md:-translate-x-4;

  @screen md {
    padding-top: var(--fixed-header-height, 0);
  }
}

.carousel-slide.is-active,
.carousel-slide.is-editmode {
  @apply pointer-events-auto relative z-10 opacity-100;

  .img-container {
    @apply scale-100 motion-safe:delay-300;
  }

  .text-container {
    @apply opacity-100 motion-safe:transition-all md:translate-x-0 motion-safe:md:delay-300 motion-safe:md:duration-700;
  }
}
</style>
